import React from 'react';
import {
  Autocomplete,
  Chip,
  LinearProgress,
  ListSubheader,
  Popper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import Grid from '@mui/material/Unstable_Grid2';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { makeStyles, styled } from '@mui/styles';
import { CustomScrollbars } from '../Table/CustomScrollbarsVirtualList';
import T from 'i18n';
import PerfectScrollbar from 'react-perfect-scrollbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];

  const inlineStyle = {
    ...style,
    width: 'max-content',
    lineHeight: style.height + 'px',
    top: style.top as number,
    fontWeight: 'lighter',
    zIndex: 0,
  };

  if (
    dataSet[3] &&
    typeof dataSet[3] === 'object' &&
    'group' in (dataSet[3] as any) &&
    (dataSet[3] as any).group
  ) {
    // Determine selection state for the whole group to style the header
    const selectedValues = dataSet[4] || [];
    const groupValKey = dataSet[7];
    const currentIds =
      (groupValKey ? selectedValues.map((val: any) => val[groupValKey]) : selectedValues) || [];

    const thisGroup = dataSet[3] as any;
    const thisLevel = thisGroup.level || 0;
    const descendantIds: any[] = [];

    for (let i = index + 1; i < (data as any[]).length; i++) {
      const nextTuple: any = (data as any[])[i];
      const nextOption: any = nextTuple && nextTuple[3];
      if (!nextOption) continue;
      const isGroup = typeof nextOption === 'object' && 'group' in nextOption && nextOption.group;
      const nextLevel =
        (nextOption &&
          typeof nextOption === 'object' &&
          'level' in nextOption &&
          nextOption.level) ||
        0;
      if (nextLevel <= thisLevel) break;
      if (!isGroup) {
        const id = groupValKey ? nextOption[groupValKey] : nextOption;
        if (id !== undefined && id !== null) descendantIds.push(id);
      }
    }
    const groupSelected =
      descendantIds.length > 0 && descendantIds.every((id: any) => currentIds.includes(id));

    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        style={{
          ...inlineStyle,
          marginLeft: `${dataSet[3].level || 0}rem`,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
        }}
        onClick={() => {
          const value = dataSet[4] || [];

          const onChange = dataSet[5];
          const setFilters = dataSet[6];
          const valKey = dataSet[7];
          const filterCategory = dataSet[8];
          const filterKey = dataSet[9];

          const currentIdsLeaf = (valKey ? value.map((val: any) => val[valKey]) : value) || [];

          // Compute descendant leaf item ids for this group based on levels in the virtualized data
          const clickGroup = dataSet[3] as any;
          const clickLevel = clickGroup.level || 0;
          const clickDescendantIds: any[] = [];

          for (let i = index + 1; i < (data as any[]).length; i++) {
            const nextTuple: any = (data as any[])[i];
            const nextOption: any = nextTuple && nextTuple[3];

            if (!nextOption) continue;

            const isGroup =
              typeof nextOption === 'object' && 'group' in nextOption && nextOption.group;
            const nextLevel =
              (nextOption &&
                typeof nextOption === 'object' &&
                'level' in nextOption &&
                nextOption.level) ||
              0;

            // Stop when we reach a sibling or parent level (group or leaf)
            if (nextLevel <= clickLevel) break;

            // Collect only leaf items (non-group entries) within deeper levels
            if (!isGroup) {
              const id = valKey ? nextOption[valKey] : nextOption;
              if (id !== undefined && id !== null) clickDescendantIds.push(id);
            }
          }

          const groupSet = new Set(clickDescendantIds);
          const allSelected =
            clickDescendantIds.length > 0 &&
            clickDescendantIds.every((id: any) => currentIdsLeaf.includes(id));

          const newValue = allSelected
            ? currentIdsLeaf.filter((id: any) => !groupSet.has(id))
            : Array.from(new Set([...(currentIdsLeaf as any[]), ...clickDescendantIds]));

          if (onChange) {
            onChange(newValue);
          }
          if (setFilters) {
            setFilters({
              ...filterCategory,
              [filterKey]: newValue,
            });
          }
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {groupSelected ? (
            <CheckCircleIcon color="primary" fontSize="small" style={{ marginRight: 8 }} />
          ) : null}
          {dataSet[1]}
        </span>
      </ListSubheader>
    );
  }

  // For leaf items, show a leading check icon when selected
  const actualValueForLeaf = dataSet[4] || [];
  const valKeyForLeaf = dataSet[7];
  const optionForLeaf: any = dataSet[3];
  const isLeafSelected = Array.isArray(actualValueForLeaf)
    ? valKeyForLeaf
      ? actualValueForLeaf.some(
          (v: any) => v && optionForLeaf && v[valKeyForLeaf] === optionForLeaf[valKeyForLeaf],
        )
      : actualValueForLeaf.includes(optionForLeaf)
    : false;

  return (
    <Typography
      key={index}
      component="li"
      {...dataSet[0]}
      noWrap
      style={{
        ...inlineStyle,
        marginLeft: `${
          dataSet[3] && typeof dataSet[3] === 'object' && (dataSet[3] as any).level
            ? (dataSet[3] as any).level
            : 0
        }rem`,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {isLeafSelected ? (
          <CheckCircleIcon color="primary" fontSize="small" style={{ marginRight: 8 }} />
        ) : null}
        {dataSet[1]}
      </span>
    </Typography>
  );
}

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);

  return (
    <div ref={ref} {...props} {...outerProps}>
      <CustomScrollbars
        {...(props as any)}
        style={{ height: 'calc(100% - 1px)' }}
        forwardedRef={ref}
      />
    </div>
  );
});

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactElement[] = [];

    (children as React.ReactElement[]).forEach(
      (item: React.ReactElement & { children?: React.ReactElement[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      },
    );

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 30 : 42;

    const getChildSize = (child: React.ReactElement) => {
      if (child && 'group' in child) {
        return 42;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * 8}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={(index) => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);

const useStyles = makeStyles(() => ({
  noBorder: {
    border: 'none',
  },
  noPadding: {
    padding: 0,
  },
  inline: {
    '& .MuiInputBase-root': {
      flexWrap: 'nowrap',
    },
    '& .MuiButtonBase-root': {
      height: '1.6rem',
    },
  },
  root: {
    '& .MuiOutlinedInput-root': {
      padding: '0 9px',
    },
    '& .Mui-readOnly': {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
    },
  },
}));

export default React.forwardRef(
  (
    {
      idx,
      originalArray,
      filterName,
      filterKey,
      valKey,
      labelKey,
      setFilters,
      filterCategory,
      loading,
      onChange,
      value,
      inline,
      placeholder,
      hideLabel,
    }: {
      idx?: number;
      originalArray: any;
      filterName?: string;
      filterKey: string;
      valKey?: string;
      labelKey?: string;
      filterCategory?: any;
      setFilters?: (newFilterCategory: any) => void;
      loading?: boolean | null;
      onChange?: any;
      value?: any;
      inline?: boolean;
      placeholder?: string;
      hideLabel?: boolean;
    },
    ref: any,
  ) => {
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    const classes = useStyles();

    const actualValue = value
      ? originalArray.filter((val: any) => value.includes(valKey ? val[valKey] : val))
      : valKey
        ? (originalArray || []).filter(
            (val: any) =>
              filterCategory &&
              filterCategory[filterKey] &&
              filterCategory[filterKey].includes(val[valKey]),
          )
        : (filterCategory && filterCategory[filterKey]) || [];
    return (
      <React.Fragment key={idx}>
        {!hideLabel && (
          <Grid xs={3}>
            <Typography
              style={{
                marginLeft: 0,
                alignSelf: 'center',
                marginTop: '0.5rem',
                fontWeight: 'bold',
              }}
            >
              {T.translate(filterName || filterKey)}
            </Typography>
          </Grid>
        )}
        <Grid xs={hideLabel ? 12 : 9} ref={ref}>
          {loading ? (
            <LinearProgress
              sx={{
                marginTop: '1rem',
              }}
            />
          ) : (
            <Autocomplete<any, true, false, false>
              multiple
              inputValue={inputValue}
              onClose={() => {
                setInputValue('');
                setIsFocused(false);
              }}
              onOpen={() => {
                setIsFocused(true);
              }}
              onInputChange={(event, val, reason) => {
                if (reason !== 'reset') {
                  setInputValue(val);
                  setIsFocused(true);
                }
              }}
              placeholder={placeholder}
              size="small"
              options={originalArray || []}
              getOptionLabel={(option: any) => (labelKey ? option[labelKey] : option)}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  // Only show placeholder when focused
                  placeholder={isFocused ? placeholder : ''}
                  variant="standard"
                  // Add these handlers
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    // Only set unfocused if not clicking within the dropdown
                    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
                      setIsFocused(false);
                    }
                  }}
                />
              )}
              value={actualValue}
              onChange={(e, newValue: any) => {
                if (onChange) {
                  onChange(valKey ? newValue.map((val: any) => val[valKey]) : newValue);
                }
                if (setFilters) {
                  setFilters({
                    ...filterCategory,
                    [filterKey]: valKey ? newValue.map((val: any) => val[valKey]) : newValue,
                  });
                }
              }}
              ListboxComponent={ListboxComponent}
              disableListWrap
              PopperComponent={StyledPopper}
              renderOption={(props, option: any, state) => {
                return [
                  props,
                  labelKey ? option[labelKey] : option,
                  state.index,
                  option,
                  actualValue,
                  onChange,
                  setFilters,
                  valKey,
                  filterCategory,
                  filterKey,
                ] as React.ReactNode;
              }}
              renderGroup={(params) => params as any}
              classes={{ root: inline ? classes.inline : '' }}
              renderTags={(values, getTagProps, owner) => (
                <PerfectScrollbar
                  options={{
                    suppressScrollX: true,
                    suppressScrollY: actualValue.length < 5 ? true : false,
                  }}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    maxHeight: 120,
                    overflowY: 'auto',
                    maxWidth: 'calc(100% - 2rem)',
                    margin: '0.25rem',
                    marginRight: '1rem',
                  }}
                >
                  {values &&
                    values.map((option, index) => {
                      const { key, ...chipTagProps } = getTagProps({ index }) as any;
                      const chipKey = valKey ? (option as any)[valKey] : (key ?? index);
                      return (
                        <Chip
                          key={chipKey}
                          label={owner.getOptionLabel(option)}
                          size={owner.size}
                          {...chipTagProps}
                          {...owner.ChipProps}
                        />
                      );
                    })}
                </PerfectScrollbar>
              )}
            />
          )}
        </Grid>
      </React.Fragment>
    );
  },
);
